import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useLocation, useNavigate } from 'react-router-dom';

import DnaBackground from '../components/DnaBackground';
import ResultsBackgroundElements from '../components/ResultsBackgroundElements';
import Seo from '../components/Seo';
import SiteFooter from '../components/SiteFooter';
import { getResults } from '../utils/profileCalculator';
import { founderTypes } from '../data/founderTypes';
import { typePlaybooks } from '../data/typePlaybooks';
import { addCheckInEntry } from '../utils/checkInStorage';
import { trackEvent } from '../utils/analytics';
import { formatFounderTitle } from '../utils/textUtils';

const Page = styled.div`
  min-height: 100vh;
  padding: 2rem;
  position: relative;
`;

const Content = styled.div`
  max-width: 900px;
  margin: 0 auto;
`;

const Card = styled(motion.div)`
  background-color: rgba(18, 18, 18, 0.7);
  border-radius: var(--border-radius);
  padding: 2rem;
  margin-bottom: 1.5rem;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.15);
`;

const Title = styled.h1`
  font-size: 2.2rem;
  margin-bottom: 0.75rem;
  color: var(--text-primary);
  font-weight: 700;
  letter-spacing: -0.5px;
`;

const Description = styled.p`
  color: var(--text-secondary);
  line-height: 1.6;
  font-size: 1.05rem;
  margin-bottom: 1.25rem;
`;

const QuestionBlock = styled.div`
  margin-top: 1.25rem;
`;

const QuestionLabel = styled.label`
  display: block;
  color: var(--text-primary);
  font-weight: 600;
  margin-bottom: 0.5rem;
`;

const Select = styled.select`
  width: 100%;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: var(--border-radius);
  color: var(--text-primary);
  padding: 0.9rem 1rem;
  font-size: 1rem;

  &:focus {
    outline: none;
    border-color: rgba(var(--primary-rgb), 0.6);
  }
`;

const ButtonRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-top: 1.5rem;

  @media (min-width: 520px) {
    flex-direction: row;
  }
`;

const PrimaryButton = styled(motion.button)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(90deg, var(--primary-color), var(--secondary-color));
  color: white;
  font-weight: 600;
  font-size: 1.05rem;
  padding: 1rem 1.25rem;
  border-radius: var(--border-radius);
  cursor: pointer;
  width: 100%;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const SecondaryButton = styled(motion.button)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.15);
  color: var(--text-primary);
  font-weight: 600;
  font-size: 1.05rem;
  padding: 1rem 1.25rem;
  border-radius: var(--border-radius);
  cursor: pointer;
  width: 100%;
`;

const List = styled.ul`
  color: var(--text-secondary);
  line-height: 1.6;
  padding-left: 1.25rem;
  margin: 0.75rem 0 0;
`;

const getSuggestedFocus = ({ playbook, energy, clarity, momentum }) => {
  // Simple rules, easy to understand and easy to iterate later.
  if (!playbook) return null;
  if (clarity === 'low') return playbook.decisionChecklist?.[0] || playbook.threeStepWeekPlan?.[0]?.title || null;
  if (momentum === 'stuck') return playbook.threeStepWeekPlan?.[1]?.title || playbook.threeStepWeekPlan?.[0]?.title || null;
  if (energy === 'low') return playbook.threeStepWeekPlan?.[2]?.title || playbook.threeStepWeekPlan?.[0]?.title || null;
  return playbook.threeStepWeekPlan?.[0]?.title || null;
};

const CheckIn = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [energy, setEnergy] = useState('');
  const [clarity, setClarity] = useState('');
  const [momentum, setMomentum] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [savedEntry, setSavedEntry] = useState(null);

  const resultsData = useMemo(() => getResults(), []);
  const profile = resultsData?.profile || null;

  const primaryTypeId = profile?.primaryType ? profile.primaryType.toLowerCase() : null;
  const primaryType = primaryTypeId ? founderTypes[primaryTypeId] : null;
  const playbook = primaryTypeId ? typePlaybooks[primaryTypeId] : null;

  const canSubmit = energy && clarity && momentum;

  const handleSubmit = () => {
    if (!canSubmit) return;

    const suggestedFocus = getSuggestedFocus({ playbook, energy, clarity, momentum });
    const entry = {
      createdAt: new Date().toISOString(),
      primaryTypeId,
      answers: { energy, clarity, momentum },
      suggestedFocus,
    };

    addCheckInEntry(entry);
    trackEvent('checkin_completed', {
      primaryType: primaryTypeId,
      energy,
      clarity,
      momentum,
      suggestedFocus: suggestedFocus || null,
    });
    setSavedEntry(entry);
    setSubmitted(true);
  };

  const handleBackToResults = () => navigate('/results');

  if (!profile || !primaryTypeId || !primaryType) {
    return (
      <Page>
        <Seo
          title="Weekly check in"
          description="Do a quick weekly check in with 3 questions and get one focus for the next 7 days."
          path={location.pathname}
          imagePath="/favicon.svg"
        />
        <DnaBackground />
        <ResultsBackgroundElements />
        <Content>
          <Card initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
            <Title>Weekly check in</Title>
            <Description>
              Take the quiz first so we can tailor your check in to your founder profile.
            </Description>
            <ButtonRow>
              <PrimaryButton onClick={() => navigate('/')} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                Go to quiz
              </PrimaryButton>
              <SecondaryButton onClick={handleBackToResults} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                Back to results
              </SecondaryButton>
            </ButtonRow>
          </Card>
        </Content>
      </Page>
    );
  }

  return (
    <Page>
      <Seo
        title="Weekly check in"
        description="Do a quick weekly check in with 3 questions and get one focus for the next 7 days."
        path={location.pathname}
        imagePath="/favicon.svg"
      />
      <DnaBackground />
      <ResultsBackgroundElements />
      <Content>
        <Card initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
          <Title>Weekly check in</Title>
          <Description>
            Quick check in for your profile: <strong>{formatFounderTitle(primaryType.title)}</strong>.
            Answer 3 questions and we will suggest one focus for the next 7 days.
          </Description>

          {!submitted ? (
            <>
              <QuestionBlock>
                <QuestionLabel htmlFor="energy">How is your energy this week?</QuestionLabel>
                <Select id="energy" value={energy} onChange={(e) => setEnergy(e.target.value)}>
                  <option value="" disabled>Select one</option>
                  <option value="high">High</option>
                  <option value="ok">OK</option>
                  <option value="low">Low</option>
                </Select>
              </QuestionBlock>

              <QuestionBlock>
                <QuestionLabel htmlFor="clarity">How clear is your next step?</QuestionLabel>
                <Select id="clarity" value={clarity} onChange={(e) => setClarity(e.target.value)}>
                  <option value="" disabled>Select one</option>
                  <option value="clear">Clear</option>
                  <option value="somewhat">Somewhat clear</option>
                  <option value="low">Not clear</option>
                </Select>
              </QuestionBlock>

              <QuestionBlock>
                <QuestionLabel htmlFor="momentum">How is execution momentum?</QuestionLabel>
                <Select id="momentum" value={momentum} onChange={(e) => setMomentum(e.target.value)}>
                  <option value="" disabled>Select one</option>
                  <option value="moving">Moving</option>
                  <option value="mixed">Mixed</option>
                  <option value="stuck">Stuck</option>
                </Select>
              </QuestionBlock>

              <ButtonRow>
                <PrimaryButton
                  onClick={handleSubmit}
                  disabled={!canSubmit}
                  whileHover={{ scale: canSubmit ? 1.02 : 1 }}
                  whileTap={{ scale: canSubmit ? 0.98 : 1 }}
                >
                  Save check in
                </PrimaryButton>
                <SecondaryButton onClick={handleBackToResults} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                  Back to results
                </SecondaryButton>
              </ButtonRow>
            </>
          ) : (
            <>
              <Description>
                Saved. Your focus for the next 7 days:
              </Description>
              <Card
                style={{ marginBottom: 0 }}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.25 }}
              >
                <p style={{ margin: 0, color: 'var(--text-primary)', fontWeight: 700, fontSize: '1.15rem' }}>
                  {savedEntry?.suggestedFocus || playbook?.threeStepWeekPlan?.[0]?.title || 'Focus on one small win'}
                </p>
                {playbook?.decisionChecklist?.length > 0 && (
                  <>
                    <p style={{ marginTop: '0.75rem', color: 'var(--text-secondary)' }}>
                      If you feel stuck, use this checklist:
                    </p>
                    <List>
                      {playbook.decisionChecklist.slice(0, 4).map((item) => (
                        <li key={item}>{item}</li>
                      ))}
                    </List>
                  </>
                )}
              </Card>
              <ButtonRow>
                <PrimaryButton onClick={handleBackToResults} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                  Back to results
                </PrimaryButton>
              </ButtonRow>
            </>
          )}
        </Card>
        <SiteFooter />
      </Content>
    </Page>
  );
};

export default CheckIn;

